import React from "react";
import { Table, Button, Card, Badge, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  useGetApplicationsQuery,
  useUpdateApplicationStatusMutation,
  useDeleteApplicationMutation,
} from "../../../redux/api/CareersApi/careersApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { FaFileDownload, FaTrash, FaEye, FaUserFriends } from "react-icons/fa";
import Swal from "sweetalert2";
import { message } from "antd";

const ApplicationsList = () => {
  const navigate = useNavigate();
  const { data: applications, isLoading } = useGetApplicationsQuery(undefined, {
    pollingInterval: 15000,
  });
  const [updateStatus] = useUpdateApplicationStatusMutation();
  const [deleteApplication] = useDeleteApplicationMutation();

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  const handleStatusChange = async (id, status) => {
    try {
      await updateStatus({ id, status }).unwrap();
      message.success("Status updated");
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the application and resume.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteApplication(id).unwrap();
        Swal.fire("Deleted!", "Application has been removed.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete application", "error");
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Submitted":
        return "secondary";
      case "Reviewing":
        return "info";
      case "Shortlisted":
        return "success";
      case "Rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaUserFriends className="text-primary me-2" />
            Job Applications
          </h2>
          <p className="text-muted mb-0">Review and manage candidates</p>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
        <div className="table-responsive">
          <Table hover className="align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4">Applicant</th>
                <th>Applied For</th>
                <th>Experience</th>
                <th>Status</th>
                <th>Received At</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications?.map((app) => (
                <tr key={app.id}>
                  <td className="ps-4 py-3">
                    <div className="fw-bold text-dark">{app.fullName}</div>
                    <div className="text-muted small">{app.email}</div>
                    <div className="text-muted extra-small">{app.phone}</div>
                  </td>
                  <td>
                    <Badge bg="light" className="text-dark border">
                      {app.job?.title || "Unknown Position"}
                    </Badge>
                  </td>
                  <td>{app.totalExperience} Yrs</td>
                  <td>
                    <Form.Select
                      size="sm"
                      className={`text-white bg-${getStatusBadge(app.status)} border-0`}
                      value={app.status}
                      onChange={(e) =>
                        handleStatusChange(app.id, e.target.value)
                      }
                    >
                      <option value="Submitted">Submitted</option>
                      <option value="Reviewing">Reviewing</option>
                      <option value="Shortlisted">Shortlisted</option>
                      <option value="Rejected">Rejected</option>
                    </Form.Select>
                  </td>
                  <td>
                    <div className="small text-dark">
                      {new Date(app.createdAt).toLocaleDateString()}
                    </div>
                    <div className="extra-small text-muted">
                      {new Date(app.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="white"
                        size="sm"
                        as="a"
                        href={app.resumePath}
                        target="_blank"
                        className="shadow-sm border"
                        title="View Resume"
                      >
                        <FaFileDownload className="text-success" />
                      </Button>
                      <Button
                        variant="white"
                        size="sm"
                        onClick={() =>
                          navigate(`/admin/application-detail/${app.id}`)
                        }
                        className="shadow-sm border"
                      >
                        <FaEye className="text-primary" />
                      </Button>
                      <Button
                        variant="white"
                        size="sm"
                        onClick={() => handleDelete(app.id)}
                        className="shadow-sm border"
                      >
                        <FaTrash className="text-danger" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!applications || applications.length === 0) && !isLoading && (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    No applications received yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default ApplicationsList;
